/**
 * @file useLegalDraftForm.ts
 * @description Поля черновика документа и их проверка (phase19.16,
 * SB139–SB141): название, адрес (только у нового), описание, текст, способ
 * показа PDF.
 *
 * Общий контракт `value / onChange` для секций карточки: секция получает
 * `values` и `set(patch)`. «Есть несохранённые правки» — сравнение с
 * карточкой, пришедшей от API (`base`); после сохранения страница вызывает
 * `reset(новая карточка)`. Проверки — правила shared (адрес, пределы),
 * чтобы кнопка не была активной там, где API ответит 400/422.
 */

import {
  LEGAL_DESCRIPTION_MAX_LENGTH,
  LEGAL_ERROR_MESSAGES,
  LEGAL_TITLE_MAX_LENGTH,
  legalKindAllowedForSlug,
  legalSlugIssue,
  type LegalDocumentDto,
  type LegalDocumentKind,
  type LegalPdfDisplay,
} from "@chaiberi/shared";
import { useCallback, useMemo, useState } from "react";

export interface LegalDraftValues {
  title: string;
  slug: string;
  description: string;
  contentHtml: string;
  pdfDisplay: LegalPdfDisplay;
}

/** Ошибки полей: текст под полем. */
export type LegalDraftErrors = Partial<Record<"title" | "slug" | "description", string>>;

export const EMPTY_LEGAL_DRAFT: LegalDraftValues = { title: "", slug: "", description: "", contentHtml: "", pdfDisplay: "INLINE" };

/** Значения формы из карточки API. */
export function legalDraftValuesOf(doc: LegalDocumentDto): LegalDraftValues {
  return {
    title: doc.title,
    slug: doc.slug,
    description: doc.description ?? "",
    contentHtml: doc.contentHtml ?? "",
    pdfDisplay: doc.pdfDisplay,
  };
}

/** Проверка полей; `isNew` — адрес ещё можно менять. */
export function validateLegalDraft(values: LegalDraftValues, kind: LegalDocumentKind, isNew: boolean): LegalDraftErrors {
  const errors: LegalDraftErrors = {};
  const title = values.title.trim();
  if (!title) errors.title = "Введите название";
  else if (title.length > LEGAL_TITLE_MAX_LENGTH) errors.title = `Не длиннее ${LEGAL_TITLE_MAX_LENGTH} знаков`;
  if (isNew) {
    const issue = legalSlugIssue(values.slug.trim());
    if (!values.slug.trim()) errors.slug = "Введите адрес";
    else if (issue) errors.slug = LEGAL_ERROR_MESSAGES[issue];
    else if (!legalKindAllowedForSlug(values.slug.trim(), kind)) errors.slug = LEGAL_ERROR_MESSAGES.legal_offer_text_only;
  }
  if (values.description.length > LEGAL_DESCRIPTION_MAX_LENGTH) {
    errors.description = `Не длиннее ${LEGAL_DESCRIPTION_MAX_LENGTH} знаков`;
  }
  return errors;
}

/**
 * Ошибки, которые уже пора показать: «Введите …» у пустого поля — только
 * после первой попытки сохранить (иначе новая форма встречает красными
 * полями); ошибки формата — сразу при вводе.
 */
export function visibleDraftErrors(errors: LegalDraftErrors, values: LegalDraftValues, attempted: boolean): LegalDraftErrors {
  if (attempted) return errors;
  const out: LegalDraftErrors = { ...errors };
  if (!values.title.trim()) delete out.title;
  if (!values.slug.trim()) delete out.slug;
  return out;
}

/** Изменённые поля для PATCH: только то, что относится к виду документа. */
export function legalDraftPatch(values: LegalDraftValues, base: LegalDraftValues, kind: LegalDocumentKind) {
  return {
    ...(values.title.trim() !== base.title ? { title: values.title.trim() } : {}),
    ...(kind !== "TEXT" && values.description.trim() !== base.description ? { description: values.description.trim() || null } : {}),
    ...(kind === "TEXT" && values.contentHtml !== base.contentHtml ? { contentHtml: values.contentHtml } : {}),
    ...(kind === "PDF" && values.pdfDisplay !== base.pdfDisplay ? { pdfDisplay: values.pdfDisplay } : {}),
  };
}

export interface LegalDraftForm {
  values: LegalDraftValues;
  set: (patch: Partial<LegalDraftValues>) => void;
  /** Есть правки, не отправленные на сервер. */
  dirty: boolean;
  /** Есть правки, из которых получится новая редакция (способ показа PDF редакцию не создаёт). */
  revisionDirty: boolean;
  /** Поля для PATCH. */
  patch: ReturnType<typeof legalDraftPatch>;
  reset: (doc: LegalDocumentDto) => void;
}

export function useLegalDraftForm(doc: LegalDocumentDto | null, kind: LegalDocumentKind, initial: LegalDraftValues = EMPTY_LEGAL_DRAFT): LegalDraftForm {
  const [base, setBase] = useState<LegalDraftValues>(() => (doc ? legalDraftValuesOf(doc) : initial));
  const [values, setValues] = useState<LegalDraftValues>(base);
  // Карточка пришла позже первой отрисовки (загрузка) — подхватываем её один раз по id.
  const [loadedId, setLoadedId] = useState<string | null>(doc?.id ?? null);
  if (doc && doc.id !== loadedId) {
    const next = legalDraftValuesOf(doc);
    setLoadedId(doc.id);
    setBase(next);
    setValues(next);
  }

  const set = useCallback((patch: Partial<LegalDraftValues>) => setValues((prev) => ({ ...prev, ...patch })), []);
  const reset = useCallback((next: LegalDocumentDto) => {
    const v = legalDraftValuesOf(next);
    setLoadedId(next.id);
    setBase(v);
    setValues(v);
  }, []);
  const patch = useMemo(() => legalDraftPatch(values, base, kind), [values, base, kind]);
  const revisionDirty = Object.keys(patch).some((key) => key !== "pdfDisplay");
  return { values, set, dirty: Object.keys(patch).length > 0, revisionDirty, patch, reset };
}
